import React, { createContext, useContext, useMemo, useRef } from 'react';
import { serverTimestamp } from 'firebase/firestore';
import { writeBatch } from './writes';
import { docRef, COLLECTIONS } from './paths';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { useSettings } from './useSettings';
import { useEvents } from './useEvents';
import { useAttendance } from './useAttendance';
import {
  bookingWindow, bookedMinutesFor, creditSummary, todayISO,
  planSetCredits, makeupAdjustmentId, makeupAdjustmentDocument,
  MAKEUP_CREDITS_MAX, MAKEUP_REASON_MAX,
} from '../domain/makeup';
import { useCollectionWindow } from './useCollectionWindow';

// Make-up credits. Issued and withdrawn by the attendance writer
// (useAttendance), because a credit is usually a consequence of an attendance
// decision, and granted or taken back **by hand** through `setCredits` below
// (#178), the one writer in this file.
//
// The studio's make-up policy — a cap on how many credits a student may hold,
// and an age at which an unused one lapses — is applied when the entries are
// folded, not when they are written (domain/makeup.js says why).
// SettingsProvider sits above this provider in App.jsx.

const MakeupContext = createContext(null);
const TRACK_LIMIT = 5000;

// The bound on the number a teacher may type into `Set Make-Up Credits`.
//
// **A batch limit, and ours.** MMS's field is a plain number with no stated
// ceiling (#178). Here one credit is one document, so a move from 0 to N is N
// writes, and a Firestore batch holds 500.
//
// It bounds **credits — the number in the box** (#352), not minutes: the
// document count is driven by credits. `setCredits` writes at most one entry
// per credit-length of the move (a 2.5-credit grant is two full-lesson entries
// and a half-lesson one), so the largest move is 201 documents, whatever the
// studio's lesson length.
//
// The studio policy's `maxCredits` is a different number: folded on read, and
// raising it brings suppressed credits back. This one refuses a keystroke.
// (The value is `MAKEUP_CREDITS_MAX` in src/domain/makeup.js, re-exported
// below.)

// `sourceType` on a hand-made adjustment. The attendance writer writes
// `'attendance'`; this tells the two apart on documents that otherwise look
// identical.
// (`ADJUST_SOURCE`, in src/domain/makeup.js.)

// The reason box. Bounded here and in `firestore.rules` — one short of the
// rules' `< 500`, which is the pairing every other bounded text field in this
// app has (see STUDENT_TEXT_MAX in domain/people.js): the field declines the
// keystroke rather than the save failing as a bare permission error.
// (`MAKEUP_REASON_MAX`, in src/domain/makeup.js.)
export { MAKEUP_CREDITS_MAX, MAKEUP_REASON_MAX };

export function MakeupProvider({ children }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  // ── The calendar half of the fold (#363) ──────────────────────────────────
  //
  // A booking is derived from the schedule rather than written to this
  // collection (`bookedMinutesFor` in domain/makeup.js), so this provider needs
  // the occurrences and the attendance records as well as its own entries.
  // `App.jsx` mounts `AttendanceProvider` and `EventsProvider` **above** this
  // one; moving `MakeupProvider` out from under either throws from
  // `useEvents`/`useAttendance`.
  const { occurrencesBetween } = useEvents();
  const { statusFor } = useAttendance();

  // The make-up entries window, from whichever read path serves this session
  // (#207 U5). `withMetadata: true` keeps `includeMetadataChanges` on the
  // snapshot arm. Rows carry `pendingWrite` and `partial` from the default row;
  // the fold reads neither.
  const read = useCollectionWindow({
    uid: user?.uid,
    collection: COLLECTIONS.makeupEntries,
    limit: TRACK_LIMIT,
    withMetadata: true,
  });
  const entries = read.docs;

  // Whether `entries` holds *this* user's credits — false before the first read
  // and false again the moment the user changes. `setCredits` computes a delta
  // against the folded balance, so a write issued before the first snapshot
  // would be a delta against a balance this app has not seen: "set to 2" would
  // read the total as 0 and grant two on top of however many the student
  // already had. The same guard as `useTags`'s.
  const hasRead = read.hasRead;
  const hasReadRef = useRef(false);
  hasReadRef.current = hasRead;

  const byStudent = useMemo(() => {
    const m = new Map();
    for (const e of entries) {
      if (!m.has(e.studentId)) m.set(e.studentId, []);
      m.get(e.studentId).push(e);
    }
    return m;
  }, [entries]);

  const policy = settings.makeup;

  // The window a booking can be found in, asked for once per render of this
  // provider rather than once per student: `occurrencesBetween` caches by
  // window inside `useEvents`, so the students list folding a column for forty
  // students expands the calendar once.
  //
  // `todayISO()` is read here rather than inside the fold so that every
  // student's summary in one render agrees about what "today" is — a list
  // rendered across midnight would otherwise have one row on each side of it.
  const asOf = todayISO();
  // `bookingWindow` rather than `addDays(asOf, BOOKING_HORIZON_DAYS)` inline,
  // so test/domain/makeup.test.js can assert the horizon (#372); this file
  // cannot be rendered by `test:ui`.
  const bookable = useMemo(
    () => occurrencesBetween(...bookingWindow(asOf))
      // Cheap and it is the point of the window: almost no occurrence carries
      // the flag, and everything downstream walks this list per student.
      .filter((o) => o.requiresMakeupCredit === true),
    [occurrencesBetween, asOf],
  );

  const value = useMemo(() => ({
    entries,
    // Exposed because every window hook has it, not for any one caller;
    // nothing turns on this flag here (a delete asks the server, #318).
    loading: read.loading,
    // Distinct from `loading`, which is "has the first read finished". This is
    // "are these *this* user's credits", which is what `setCredits` has to know
    // before it may compute a delta against them.
    loaded: hasRead,
    // The read stopped before it answered (#647) — src/domain/reading.js.
    failed: read.failed,
    policy,
    // ── `lessonMin` is the caller's to supply, with no default (#352) ──────
    //
    // The number a teacher sees is this student's minutes over this student's
    // current lesson length, which lives on the student row every caller
    // already holds (`student.durationMin`, stored `defaultDurationMin`,
    // renamed by data/useStudents.jsx). A caller that omits it gets `null` for
    // the four credit numbers, and the screens dash them.
    //
    // ── `except` (#363) ────────────────────────────────────────────────────
    //
    // A flagged, unrecorded occurrence books a credit forward, but MMS offers
    // the `Use Make-Up Credit` statuses on a flagged occurrence at a balance of
    // one: a lesson's own reservation is not held against the lesson it is for.
    // So `except` is an occurrence id to leave out of the booking fold, and
    // `useAttendanceForm` passes the occurrence it is open on. Everything else
    // — the students-list column, the profile card, the report — omits it and
    // sees the whole commitment, which is what the student rail shows.
    summaryFor: (studentId, lessonMin, { except = null } = {}) => creditSummary(
      byStudent.get(studentId) || [], lessonMin, policy, asOf,
      // No `lessonMin`: a booking is the occurrence's own minutes or nothing
      // at all (#374, #365). `creditSummary` takes it as the divisor.
      bookedMinutesFor(bookable, { studentId, statusFor, asOf, except }),
    ),

    // ── Set Make-Up Credits (#178) ─────────────────────────────────────────
    //
    // MMS's Lesson Settings drawer has a fifth row: a number field captioned
    // `Optional`, suffixed `Credits`, showing the student's balance (#178; the
    // students list's bulk `Edit Make-Up Credits` is the other half). This is
    // the drawer half, **a knowing divergence rather than a copy.**
    //
    // MMS's credits are a **number on the student**, so its field is a plain
    // overwrite with no reason, date or author. Ours are a collection folded on
    // read (see domain/makeup.js): a stored counter is a mutable cell two
    // offline devices would both decrement. So the **control** is MMS's — one
    // number, typed over the current balance, saved by the same Save — and the
    // **write** appends the entries that make the fold land on the typed total.
    //
    // ── Absolute, not delta ───────────────────────────────────────────────
    //
    // Whether MMS reads its field as a total or a delta is not known (#178,
    // #286). **The field is seeded with the student's current Total and read as
    // the total they should end up with**, so the value in the box is the value
    // being set.
    //
    // ── The id a hand-granted credit is keyed by ──────────────────────────
    //
    // Every other entry in this collection is
    // `makeupIssueId(occurrenceId, studentId)` — a credit exists if and only if
    // an occurrence produced it — and a hand-granted credit has no occurrence
    // to key on.
    //
    // It is keyed by a **slot**: `mk_adj_<n>__<studentId>`, `n` counting up per
    // student across the whole collection and stored on the document as `slot`
    // so the next writer does not have to parse an id back. Built through
    // `makeupIssueId` with a synthetic first segment rather than a second
    // sanitiser — `adj_3` can never collide with an occurrence id, because
    // those begin `occ_`.
    //
    // What that buys, and what it does not:
    //
    //   * Two devices making the **same** decision from the same view compute
    //     the same slots and write the same documents. Setting a student to 5
    //     twice leaves five credits, not ten — the property every id in
    //     core/src/ids.rs exists for.
    //   * Two devices making **different** decisions from the same view merge
    //     rather than converge on either: A sets +3 into slots 1-3, B sets -1
    //     into slot 1, and slot 1 lands last-write-wins while 2 and 3 stand.
    //     Neither teacher gets exactly what they asked for; every document is
    //     still a real dated record with a reason on it. This is the one case
    //     the scheme does not get right.
    //
    // ── Two kinds, not a signed count ─────────────────────────────────────
    //
    // `CREDIT_KINDS` in domain/makeup.js gives each kind a fixed sign, and each
    // entry carries its minutes. Granting appends `issue`; taking back appends
    // `expire`, which that file defines as revoked by hand.
    //
    // ── The number the delta is measured against is the one on screen ──────
    //
    // `creditSummary().total`, not the raw ledger sum: the studio's cap and its
    // expiry window are folded in, and the teacher is typing over what the card
    // shows. So a student whose two oldest credits have lapsed and who now
    // reads 3 goes to 5 by appending two. Where a **cap** is in force the fold
    // still shows no more than the cap, and the drawer says so.
    //
    // ── What it returns, and why it is not a number ───────────────────────
    //
    // `{ done: true, written }` when it read the ledger and acted — `written`
    // is the delta, and **0 is a legitimate value there**: the balance was
    // already the typed total, so nothing needed writing and the control did
    // its job. `{ done: false, reason }` when it refused: `'signed-out'`,
    // `'unread'` (this is not known to be *this* user's ledger yet),
    // `'no-student'`, `'no-duration'`, or `'bounds'`.
    //
    // A bare delta would carry two meanings in 0 — nothing needed writing, or
    // the input was refused — and `SetMakeupCreditsDrawer` could not tell them
    // apart (#115, #121, #164).
    //
    // ── What Save does ────────────────────────────────────────────────────
    //
    // `planSetCredits` in src/domain/makeup.js, each entry
    // `makeupAdjustmentDocument`, so a phone sets credits with the same
    // arithmetic:
    //
    //   * A fractional total is accepted (#352): `3.33` is a real balance. The
    //     bound is on the credits typed (MAKEUP_CREDITS_MAX above), and a
    //     non-finite entry is refused.
    //   * A student with no lesson length (#352) is refused as `no-duration`:
    //     the fix is the Duration field on Lesson Settings.
    //   * The delta is measured in whole minutes against `totalMin`, which
    //     bookings do not move (#363), so saving the 3.33 the box was seeded
    //     with writes nothing — the control is safe to press twice.
    //   * The next slot is past the highest this student's hand adjustments
    //     have used, live or voided, so a voided one is never written over.
    //   * The move is chopped into lesson-lengths and a remainder (a 2.5-credit
    //     move is two full entries and a half one), so one document is one
    //     credit, `slot` counts credits, and two devices making the same
    //     decision write the same documents. The chunks sum to the move
    //     exactly.
    //   * Each entry records who granted it and carries both halves of the
    //     tombstone, a complete valid document every time (see useAttendance.jsx
    //     on why a bare `{ voidedAt }` merge is a create the rules refuse).
    //   * `written` is in **credits**, the unit the teacher typed.
    setCredits: async ({ student, total, reason = '' }) => {
      const plan = planSetCredits({
        entries: byStudent.get(student?.id) || [],
        student,
        total,
        reason,
        policy,
        asOf,
        signedIn: Boolean(user),
        hasRead: hasReadRef.current,
      });
      if (!plan.done || !plan.chunks) return plan;

      const occurredOn = todayISO();
      const batch = writeBatch(db);
      plan.chunks.forEach((durationMin, i) => {
        const slot = plan.firstSlot + i;
        batch.set(
          docRef(user.uid, COLLECTIONS.makeupEntries, makeupAdjustmentId(slot, student.id)),
          makeupAdjustmentDocument({
            kind: plan.kind, slot, durationMin, student, occurredOn, uid: user.uid, note: plan.note,
          }, serverTimestamp()),
          { merge: true },
        );
      });
      await batch.commit();
      return { done: true, written: plan.written };
    },
  }), [entries, read.loading, read.failed, hasRead, byStudent, policy, user,
    bookable, statusFor, asOf]);

  return <MakeupContext.Provider value={value}>{children}</MakeupContext.Provider>;
}

export function useMakeup() {
  const ctx = useContext(MakeupContext);
  if (!ctx) throw new Error('useMakeup must be used within a MakeupProvider');
  return ctx;
}
